import { getSupabase } from "../supabase";

import {
  normalizeAuditLogEntry,
  normalizeImpersonationEntry,
  mergeAuditEntries,
  filterAuditEntries,
  toCsv,
} from "./auditTimeline.mjs";
import type { AuditEntry, AuditFilters } from "./auditTimeline.mjs";

export type { AuditEntry, AuditFilters };
export { filterAuditEntries, toCsv };

/**
 * MER-493: fetches both audit sources and returns one merged,
 * newest-first timeline. `limit` bounds each source independently
 * (not the merged total) so a burst on one source cannot starve the
 * other out of the page.
 */
export async function fetchAuditTimeline(limit = 500): Promise<AuditEntry[]> {
  const supabase = getSupabase();
  const [adminLogResult, impersonationResult, tenantsResult] = await Promise.all([
    supabase
      .from("admin_audit_log")
      .select("id, actor_user_id, actor_email, action_type, customer_slug, payload, occurred_at")
      .order("occurred_at", { ascending: false })
      .limit(limit),
    supabase
      .from("impersonation_event")
      .select("id, staff_user_id, target_tenant_id, event_type, occurred_at")
      .order("occurred_at", { ascending: false })
      .limit(limit),
    supabase.from("tenants").select("id, slug"),
  ]);

  if (adminLogResult.error) {
    throw new Error(`fetchAuditTimeline (admin_audit_log) failed: ${adminLogResult.error.message}`);
  }
  if (impersonationResult.error) {
    throw new Error(
      `fetchAuditTimeline (impersonation_event) failed: ${impersonationResult.error.message}`,
    );
  }

  const tenantSlugById = new Map<string, string>();
  for (const t of (tenantsResult.data as Array<{ id: string; slug: string }> | null) ?? []) {
    tenantSlugById.set(t.id, t.slug);
  }

  const adminEntries = ((adminLogResult.data as Array<Record<string, unknown>> | null) ?? []).map(
    (row) => normalizeAuditLogEntry(row),
  );
  const impersonationEntries = (
    (impersonationResult.data as Array<Record<string, unknown>> | null) ?? []
  ).map((row) => normalizeImpersonationEntry(row, tenantSlugById));

  return mergeAuditEntries(adminEntries, impersonationEntries);
}

/** Triggers a browser download of the given entries as audit-log.csv. */
export function downloadAuditCsv(entries: AuditEntry[]): void {
  const csv = toCsv(entries);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `admin-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
